import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  HomeIcon,
  ChevronRightIcon,
  PlusIcon,
  ArrowPathIcon
} from '@heroicons/react/20/solid'
import { useAuthenticator } from '@aws-amplify/ui-react'
import { format, formatDistance } from 'date-fns'
import { de } from 'date-fns/locale'
import initials from 'initials'
import { BeatLoader } from 'react-spinners'

import { query } from '../api'
import { updatePartnerAppSettings } from '../api/updatePartner'
import Loader from '../components/Loader'
import Button from '../components/Button'
import DeletePartnerModal from '../components/DeletePartnerModal'
import { Notice } from '../components/experiments/ExperimentBadges'
import UsageMeter, { usageMeterModel } from '../components/pricing/UsageMeter'
import PricingEnforcementBadge, {
  PaymentKindBadge,
  contractOf,
  recommendationLabel
} from '../components/pricing/PricingEnforcementBadge'
import PricingOverrideControl from '../components/pricing/PricingOverrideControl'
import SchemaProbeNotice from '../components/pricing/SchemaProbeNotice'
import {
  checkIfAccountIsAdmin,
  classNames,
  extractErrorMessage,
  formatToCurrency
} from '../helper'
import { STAGE } from '../helper/stage'
import {
  NO_PLAN_LABEL,
  PAYMENT_KINDS,
  UNKNOWN_PLAN_LABEL,
  effectiveLimitsOf,
  paymentKind,
  planName,
  recommendPlan
} from '../helper/pricing'
import {
  ENFORCEMENT_STATES,
  describePricingFlag,
  pricingEnforcementState,
  pricingFlagOf
} from '../helper/pricing-flag'
import {
  planOf,
  selectPayments
} from '../helper/pricing-mirror/payment-selection'
import { LIMIT_STATES } from '../helper/pricing-mirror/limits'
import { useNotificationContext } from '../context/notification-context'
import { useFeatureFlags } from '../hooks/use-feature-flags'
import { usePricingUsage } from '../hooks/use-pricing-usage'
import { useSchemaCapabilities } from '../hooks/use-schema-capabilities'

/**
 * Helper function to safely parse dashboard settings
 * Handles both string (AWSJSON) and object formats
 */
const parseDashboardSettings = (dashboardSettingsRaw) => {
  try {
    if (!dashboardSettingsRaw) return {}
    return typeof dashboardSettingsRaw === 'string'
      ? JSON.parse(dashboardSettingsRaw)
      : dashboardSettingsRaw
  } catch (e) {
    // Kaputte Settings blockieren die Seite nicht — leeres Objekt als Fallback.
    return {}
  }
}

// ---------------------------------------------------------------------------
// Payments und Pricing (rein, für Tests exportiert)
// ---------------------------------------------------------------------------

// Präfix ['payment', partnerId] bleibt, damit invalidateQueries(['payment',
// partnerId]) nach dem Anlegen einer Zahlung auch diese Liste trifft
export const paymentsQueryKey = (partnerId, withPlan) => [
  'payment',
  partnerId,
  withPlan ? 'with-plan' : 'base'
]

// plan steht nur in der Query, wenn die Schema-Probe das Feld meldet. Sonst
// lehnt AppSync die ganze Query ab.
export const paymentsQueryName = (withPlan) =>
  withPlan
    ? 'paymentByPartnerIdByCreatedAtWithPlan'
    : 'paymentByPartnerIdByCreatedAt'

export const newPaymentPath = (partnerId, fromPaymentId) =>
  `/customer/${partnerId}/payment/new` +
  (fromPaymentId ? `?from=${encodeURIComponent(fromPaymentId)}` : '')

const timeOf = (iso) => {
  const time = Date.parse(iso)
  return Number.isFinite(time) ? time : -Infinity
}

// Neueste oben. Ohne gültiges createdAt ans Ende.
export const sortPaymentsDesc = (payments) =>
  (Array.isArray(payments) ? payments.filter(Boolean) : []).sort((a, b) => {
    const ta = timeOf(a.createdAt)
    const tb = timeOf(b.createdAt)
    if (ta === tb) return 0
    return tb > ta ? 1 : -1
  })

export const PAYMENT_ROLES = {
  PERIOD: 'period',
  TODAY: 'today',
  WITH_LOCK: 'with_lock',
  WITHOUT_LOCK: 'without_lock'
}

export const PAYMENT_ROLE_LABELS = {
  [PAYMENT_ROLES.PERIOD]: 'Laufzeit und Zugang',
  [PAYMENT_ROLES.TODAY]: 'Rechte heute',
  [PAYMENT_ROLES.WITH_LOCK]: 'Rechte mit Pricing-Sperre',
  [PAYMENT_ROLES.WITHOUT_LOCK]: 'Rechte ohne Pricing-Sperre'
}

export const PAYMENT_ROLE_HINTS = {
  [PAYMENT_ROLES.PERIOD]:
    'Neuestes Payment ohne Credits. Steuert Laufzeit, Trial-Ablauf und den Payment-Alert.',
  [PAYMENT_ROLES.TODAY]:
    'Von diesem Payment kommen heute Funktionen und Grenzen im Dashboard.',
  [PAYMENT_ROLES.WITH_LOCK]:
    'Gilt, sobald die Pricing-Sperre für diesen Kunden an ist.',
  [PAYMENT_ROLES.WITHOUT_LOCK]:
    'Gilt, solange die Pricing-Sperre für diesen Kunden aus ist. Das ist der Rollback.'
}

const SWITCH_KNOWN = [
  ENFORCEMENT_STATES.ACTIVE,
  ENFORCEMENT_STATES.READY,
  ENFORCEMENT_STATES.WAITING,
  ENFORCEMENT_STATES.OFF
]
const SWITCH_ON = [ENFORCEMENT_STATES.ACTIVE, ENFORCEMENT_STATES.WAITING]

/**
 * Rollen der Payments nach selectPayments aus dem Dashboard.
 * - period ist das periodPayment (Laufzeit, Trial-Ablauf, Payment-Alert)
 * - today ist das Payment, von dem heute die Rechte kommen. Bei Sperre an
 *   das Plan-Payment, sonst das legacyModePayment wie im Dashboard
 * - with_lock bzw. without_lock markieren das andere, falls es abweicht
 * Ist der Schalter nicht bekannt (Flag nicht lesbar, Teil-Rollout), gibt es
 * kein today, wenn die beiden abweichen, nur with_lock und without_lock.
 *
 * @param {object[]} payments
 * @param {string} enforcement aus pricingEnforcementState
 * @returns {Object<string, string[]>} Rollen je Payment-ID
 */
export const paymentRolesOf = (payments, enforcement) => {
  // Ohne plan weiß das OS nicht, welche Zahlung heute gilt
  if (enforcement === ENFORCEMENT_STATES.PLAN_UNKNOWN) return {}
  const { periodPayment, planPayment, legacyModePayment } =
    selectPayments(payments)
  const lockedPayment = planPayment || legacyModePayment
  const roles = {}
  const add = (payment, role) => {
    if (!payment) return
    roles[payment.id] = [...(roles[payment.id] || []), role]
  }

  add(periodPayment, PAYMENT_ROLES.PERIOD)

  if (!lockedPayment) return roles
  if (lockedPayment.id === legacyModePayment?.id) {
    add(lockedPayment, PAYMENT_ROLES.TODAY)
    return roles
  }

  if (!SWITCH_KNOWN.includes(enforcement)) {
    add(legacyModePayment, PAYMENT_ROLES.WITHOUT_LOCK)
    add(lockedPayment, PAYMENT_ROLES.WITH_LOCK)
    return roles
  }

  const switchOn = SWITCH_ON.includes(enforcement)
  add(switchOn ? lockedPayment : legacyModePayment, PAYMENT_ROLES.TODAY)
  add(
    switchOn ? legacyModePayment : lockedPayment,
    switchOn ? PAYMENT_ROLES.WITHOUT_LOCK : PAYMENT_ROLES.WITH_LOCK
  )
  return roles
}

// Paket-Spalte der Zahlungsliste
export const planColumnLabel = (payment) => {
  const kind = paymentKind(payment)
  if (kind === PAYMENT_KINDS.PLAN) return planName(planOf(payment))
  if (kind === PAYMENT_KINDS.UNKNOWN_PLAN) return UNKNOWN_PLAN_LABEL
  return NO_PLAN_LABEL
}

// price ist der Monatspreis in Cent
export const monthlyPriceLabel = (payment) => {
  const price = Number(payment?.price)
  if (payment?.price === null || payment?.price === undefined) return '-'
  if (!Number.isFinite(price)) return '-'
  return `${formatToCurrency(price / 100)} / Monat`
}

const formatDate = (iso) => {
  if (!iso) return '-'
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? '-' : format(date, 'dd.MM.yyyy')
}

const isExpired = (iso, now = new Date()) => {
  const time = Date.parse(iso)
  return Number.isFinite(time) && time < now.getTime()
}

export const RESOURCE_LABELS = {
  careers: 'Gleichzeitig offene Stellen',
  seats: 'Dashboard-Zugänge',
  talentStorage: 'Talentspeicher'
}

/**
 * Hinweise unter den Metern. Mit Paket und aktiver Sperre keiner. Mit Paket
 * ohne Sperre gilt das Limit noch nicht, außer den Zugängen, wenn das Paket
 * ohne Altvertrag schon über die bisherige Logik wirkt. Ist der Status der
 * Sperre unbekannt oder ein Teil-Rollout, sagen die Hinweise genau das statt
 * „aus". Ohne Paket greift heute nur die Zugangsgrenze, egal wie der Schalter
 * steht.
 *
 * @returns {{ careers: string|null, seats: string|null,
 *   talentStorage: string|null }}
 */
export const meterHintsFor = ({ payments, enforcement }) => {
  if (enforcement === ENFORCEMENT_STATES.PLAN_UNKNOWN) {
    const unknown =
      'Paket nicht lesbar, die Schema-Prüfung ist fehlgeschlagen. Die Zahl stammt aus der neuesten Zahlung.'
    return { careers: unknown, seats: unknown, talentStorage: unknown }
  }
  const { planPayment, legacyModePayment } = selectPayments(payments)
  const governing = planPayment || legacyModePayment

  if (!governing) {
    const none = 'Keine Zahlung hinterlegt.'
    return { careers: none, seats: none, talentStorage: none }
  }

  if (planPayment) {
    if (enforcement === ENFORCEMENT_STATES.ACTIVE) {
      return { careers: null, seats: null, talentStorage: null }
    }
    const later =
      enforcement === ENFORCEMENT_STATES.UNKNOWN
        ? 'Status der Pricing-Sperre unbekannt. Gilt, sobald die Sperre für diesen Kunden an ist.'
        : enforcement === ENFORCEMENT_STATES.PARTIAL
          ? 'Teil-Rollout. Ob die Sperre für diesen Kunden an ist, rechnet das OS nicht nach.'
          : 'Noch nicht durchgesetzt, gilt mit aktiver Pricing-Sperre.'
    const actsNow = legacyModePayment?.id === planPayment.id
    return {
      careers: later,
      seats: actsNow
        ? 'Gilt schon heute. Ohne Altvertrag sperrt das Dashboard das Einladen ab dieser Zahl.'
        : later,
      talentStorage: later
    }
  }

  return {
    careers: 'Ohne Paket nicht durchgesetzt.',
    seats: 'Bisherige Logik, Einladen ist ab dieser Zahl gesperrt.',
    talentStorage:
      'Ohne Paket nicht durchgesetzt. Im Altvertrag oft Testungen pro Jahr.'
  }
}

/**
 * Hinweis unter dem Override-Control, warum der Status nicht lesbar ist.
 * Kaputtes JSON liest auch das Dashboard nicht, es rechnet dann mit Sperre
 * aus. Ein Ladefehler betrifft nur das OS, das Dashboard lädt FEATURE_FLAGS
 * selbst.
 *
 * @param {{ isError?: boolean, isLoading?: boolean, data?: object }} flagsQuery
 * @returns {string|null}
 */
export const flagReadHint = (flagsQuery) => {
  if (flagsQuery?.isLoading) return null
  if (flagsQuery?.isError) {
    return 'Status im OS nicht geladen. Das Dashboard lädt FEATURE_FLAGS selbst, die Sperre kann dort trotzdem an sein.'
  }
  if (flagsQuery?.data?.parseError) {
    return 'FEATURE_FLAGS enthält kein gültiges JSON. Das liest auch das Dashboard nicht, es rechnet dann mit Sperre aus.'
  }
  return null
}

// Ressourcen über dem Limit (nur endliche Limits und gezählte Nutzung)
export const overLimitResources = (usage, limits) =>
  ['careers', 'seats', 'talentStorage'].filter(
    (resource) =>
      usage &&
      limits &&
      usageMeterModel({ used: usage[resource], limit: limits[resource] })
        .state === LIMIT_STATES.OVER_LIMIT
  )

// ---------------------------------------------------------------------------
// Bausteine
// ---------------------------------------------------------------------------

const ROLE_TONES = {
  [PAYMENT_ROLES.PERIOD]: 'bg-blue-50 text-blue-700 ring-blue-700/10',
  [PAYMENT_ROLES.TODAY]: 'bg-green-50 text-green-700 ring-green-600/20',
  [PAYMENT_ROLES.WITH_LOCK]: 'bg-white text-indigo-700 ring-indigo-300',
  [PAYMENT_ROLES.WITHOUT_LOCK]: 'bg-white text-gray-600 ring-gray-300'
}

const roleHintId = (role) => `payment-role-hint-${role}`

const RoleChip = ({ role, describe = true }) => (
  <span
    className={classNames(
      'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
      ROLE_TONES[role]
    )}
    title={PAYMENT_ROLE_HINTS[role]}
    aria-describedby={describe ? roleHintId(role) : undefined}
  >
    {PAYMENT_ROLE_LABELS[role]}
  </span>
)

// Sichtbare Legende unter der Zahlungsliste, nur für Rollen, die vorkommen
export const presentRolesOf = (roles) => {
  const present = new Set(Object.values(roles || {}).flat())
  return Object.values(PAYMENT_ROLES).filter((role) => present.has(role))
}

const RoleLegend = ({ roles }) => {
  const present = presentRolesOf(roles)
  if (!present.length) return null
  return (
    <dl
      className='mt-4 grid grid-cols-1 gap-2 border-t border-gray-100 pt-4 text-xs text-gray-600 sm:grid-cols-2'
      data-testid='role-legend'
    >
      {present.map((role) => (
        <div key={role} className='flex items-start gap-2'>
          <dt className='shrink-0'>
            <RoleChip role={role} describe={false} />
          </dt>
          <dd id={roleHintId(role)}>{PAYMENT_ROLE_HINTS[role]}</dd>
        </div>
      ))}
    </dl>
  )
}

const NewPaymentLink = ({ partnerId, fromPaymentId, state }) => (
  <Link
    to={newPaymentPath(partnerId, fromPaymentId)}
    state={state}
    className='inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
  >
    <PlusIcon className='-ml-0.5 mr-1.5 h-5 w-5' aria-hidden='true' />
    Neue Zahlung
  </Link>
)

const MeterSkeleton = () => (
  <div aria-hidden='true'>
    <div className='flex justify-between'>
      <div className='h-4 w-40 animate-pulse rounded bg-gray-200' />
      <div className='h-4 w-16 animate-pulse rounded bg-gray-200' />
    </div>
    <div className='mt-2 h-2 w-full animate-pulse rounded-full bg-gray-200' />
  </div>
)

const RetryButton = ({ onClick, isLoading, text = 'Erneut versuchen' }) => (
  <button
    type='button'
    onClick={onClick}
    disabled={isLoading}
    className='inline-flex items-center gap-x-1 text-sm font-medium text-indigo-600 hover:text-indigo-500 disabled:opacity-50'
  >
    <ArrowPathIcon
      className={classNames('h-4 w-4', isLoading && 'animate-spin')}
      aria-hidden='true'
    />
    {text}
  </button>
)

const ContractLine = ({ contract }) => {
  const payment = contract.payment
  const showPeriod =
    payment &&
    contract.kind !== PAYMENT_KINDS.CREDITS &&
    (payment.start_at || payment.expires_at)
  const expired = payment && isExpired(payment.expires_at)

  return (
    <div className='flex flex-wrap items-center gap-2'>
      <PaymentKindBadge payment={payment} />
      {showPeriod && (
        <span className='text-sm text-gray-700'>
          {formatDate(payment.start_at)} bis {formatDate(payment.expires_at)}
        </span>
      )}
      {showPeriod && expired && (
        <span className='text-xs font-medium text-red-700'>abgelaufen</span>
      )}
      {contract.kind === PAYMENT_KINDS.LEGACY && (
        <span className='text-xs text-gray-500'>kein Paket</span>
      )}
    </div>
  )
}

const PlanUsageSection = ({
  partnerId,
  customerName,
  payments,
  paymentsQuery,
  capabilities,
  flagsQuery,
  saveFlags,
  enforcement,
  flagPercent,
  isAdmin,
  owner
}) => {
  const navigate = useNavigate()
  const usageQuery = usePricingUsage(partnerId, {
    enabled: !paymentsQuery.isError
  })
  const { usage } = usageQuery

  const selection = selectPayments(payments)
  const { planPayment, legacyPayment, legacyModePayment, periodPayment } =
    selection
  const contract = contractOf(payments)
  const governing = planPayment || legacyModePayment
  const limits = governing ? effectiveLimitsOf(governing) : null
  const hints = meterHintsFor({ payments, enforcement })
  const currentPlanName = planPayment ? planName(planOf(planPayment)) : null

  const lockedTalents =
    planPayment && usage ? usage.lockedTalents(limits?.talentStorage) : 0
  const recommendation = usage
    ? recommendPlan({
        careers: usage.careers,
        seats: usage.seats,
        storage: usage.talentStorage
      })
    : null
  const overLimit = planPayment ? overLimitResources(usage, limits) : []
  const migrateFrom = legacyPayment || periodPayment

  const meters = [
    { resource: 'careers', used: usage?.careers },
    { resource: 'seats', used: usage?.seats },
    { resource: 'talentStorage', used: usage?.talentStorage }
  ]

  return (
    <section
      className='mt-4 scroll-mt-4'
      aria-labelledby='paket-title'
      id='paket'
    >
      <div className='bg-white shadow sm:rounded-lg'>
        <div className='px-4 py-5 sm:px-6'>
          <h2
            id='paket-title'
            className='text-lg font-medium leading-6 text-gray-900'
          >
            Paket und Nutzung
          </h2>
          <p className='mt-1 max-w-2xl text-sm text-gray-500'>
            Vertrag, Nutzung und Status der Pricing-Sperre. Gezählt wird mit
            denselben Regeln wie im Dashboard.
          </p>
        </div>

        <div className='border-t border-gray-200 px-4 py-5 sm:px-6 space-y-6'>
          {paymentsQuery.isError ? (
            <Notice tone='error'>
              Zahlungen konnten nicht geladen werden.{' '}
              {extractErrorMessage(paymentsQuery.error)}{' '}
              <RetryButton
                onClick={() => paymentsQuery.refetch()}
                isLoading={paymentsQuery.isFetching}
              />
            </Notice>
          ) : (
            <>
              <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                <div>
                  <h3 className='text-sm font-medium text-gray-500'>Vertrag</h3>
                  <div className='mt-2'>
                    <ContractLine contract={contract} />
                  </div>
                </div>
                <div>
                  <h3 className='text-sm font-medium text-gray-500'>
                    Pricing-Sperre
                  </h3>
                  <PricingEnforcementBadge
                    className='mt-2'
                    state={enforcement}
                    planName={currentPlanName}
                    percent={flagPercent}
                    showDetail
                    isLoading={flagsQuery.isLoading}
                    onRetry={() => flagsQuery.refetch()}
                    isRetrying={flagsQuery.isFetching}
                  />
                </div>
              </div>

              {capabilities.isError ? (
                <SchemaProbeNotice
                  onRetry={capabilities.refetch}
                  isRetrying={capabilities.isFetching}
                >
                  Ob diese Umgebung Payment.plan kennt, ist unklar. Die
                  Zahlungen sind ohne plan geladen und zeigen deshalb keine
                  Pakete. Auf Paket umstellen geht erst nach der Prüfung.
                </SchemaProbeNotice>
              ) : (
                !capabilities.isLoading &&
                !capabilities.paymentPlan && (
                  <Notice tone='info'>
                    Diese Umgebung kennt Payment.plan noch nicht. Pakete sind
                    nach dem Backend-Deploy verfügbar, bis dahin gelten alle
                    Zahlungen als Altvertrag.
                  </Notice>
                )
              )}

              <div>
                <div className='flex items-center justify-between'>
                  <h3 className='text-sm font-medium text-gray-500'>Nutzung</h3>
                  {usage && (
                    <RetryButton
                      onClick={() => usageQuery.refetch()}
                      isLoading={usageQuery.isFetching}
                      text='Neu zählen'
                    />
                  )}
                </div>

                {usageQuery.isError && !usage ? (
                  <Notice tone='error' className='mt-3'>
                    Nutzung konnte nicht geladen werden.{' '}
                    {extractErrorMessage(usageQuery.error)}{' '}
                    <RetryButton
                      onClick={() => usageQuery.refetch()}
                      isLoading={usageQuery.isFetching}
                    />
                  </Notice>
                ) : (
                  <div className='mt-3 grid grid-cols-1 gap-6 sm:grid-cols-3'>
                    {meters.map(({ resource, used }) =>
                      usageQuery.isLoading || !usage ? (
                        <MeterSkeleton key={resource} />
                      ) : (
                        <UsageMeter
                          key={resource}
                          label={RESOURCE_LABELS[resource]}
                          used={used}
                          limit={limits ? limits[resource] : null}
                          lockedTalents={
                            resource === 'talentStorage' ? lockedTalents : 0
                          }
                          lockPending={
                            enforcement !== ENFORCEMENT_STATES.ACTIVE
                          }
                          hint={hints[resource]}
                        />
                      )
                    )}
                  </div>
                )}
              </div>

              {!planPayment && (
                <div
                  className='rounded-md bg-gray-50 px-4 py-3'
                  data-testid='plan-recommendation'
                >
                  <dl className='text-sm'>
                    <dt className='font-medium text-gray-900'>
                      Passend laut Nutzung
                    </dt>
                    <dd className='mt-1 text-gray-700'>
                      {recommendation ? (
                        <>
                          {recommendationLabel(recommendation)}, Listenpreis{' '}
                          {formatToCurrency(recommendation.monthly)} im Monat
                          bei 12 Monaten
                        </>
                      ) : usageQuery.isError ? (
                        'Ohne Nutzung keine Empfehlung. Zähl neu, um eine zu sehen.'
                      ) : (
                        <span className='inline-flex items-center gap-x-2 text-gray-500'>
                          <span
                            className='inline-block h-4 w-40 animate-pulse rounded bg-gray-200'
                            aria-hidden='true'
                          />
                          Empfehlung nach dem Zählen
                        </span>
                      )}
                    </dd>
                  </dl>
                  {capabilities.paymentPlan && migrateFrom && (
                    <div className='mt-3'>
                      <Button.PrimaryBase
                        text='Auf Paket umstellen'
                        onClick={() =>
                          navigate(newPaymentPath(partnerId, migrateFrom.id), {
                            state: { customerName, customerId: partnerId }
                          })
                        }
                      />
                      <p className='mt-2 text-xs text-gray-500'>
                        Legt eine neue Zahlung mit Paket an. Die bisherige
                        bleibt unverändert und ist der Rollback.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {planPayment && overLimit.length > 0 && recommendation && (
                <div className='rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-900 ring-1 ring-inset ring-amber-600/20'>
                  <p>
                    Die Nutzung liegt über dem Paket (
                    {overLimit
                      .map((resource) => RESOURCE_LABELS[resource])
                      .join(', ')}
                    ). Passend laut Nutzung wäre{' '}
                    {recommendationLabel(recommendation)}.
                  </p>
                  <Link
                    to={`/customer/payment/${planPayment.id}`}
                    state={{ customerName, customerId: partnerId }}
                    className='mt-2 inline-block font-medium text-indigo-600 hover:text-indigo-500'
                  >
                    Paket-Zahlung bearbeiten
                  </Link>
                </div>
              )}
            </>
          )}

          {isAdmin && (
            <div className='border-t border-gray-100 pt-5'>
              <PricingOverrideControl
                partnerId={partnerId}
                partnerName={customerName}
                flagsQuery={flagsQuery}
                saveFlags={saveFlags}
                isAdmin={isAdmin}
                owner={owner}
                hasPlanPayment={!!planPayment}
                stage={STAGE}
              />
              {flagReadHint(flagsQuery) && (
                <p className='mt-2 text-xs text-gray-500'>
                  {flagReadHint(flagsQuery)}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Seite
// ---------------------------------------------------------------------------

const Customer = () => {
  const { id } = useParams()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { user } = useAuthenticator((context) => [context.user])
  const isAdmin = !!checkIfAccountIsAdmin(user)
  const owner =
    user?.attributes?.email ||
    user?.signInUserSession?.idToken?.payload?.email ||
    user?.username ||
    ''

  const { error } = useNotificationContext()

  const [deleteOpen, setDeleteOpen] = useState(false)

  const {
    // status,
    data: customer,
    isLoading
  } = useQuery({
    queryKey: ['customer', id],
    queryFn: () => query({ query: 'getPartner', variables: { id } })
  })

  const { data: teammember, isLoading: isLoadingTeammember } = useQuery({
    queryKey: ['customer-teammember', id],
    queryFn: () => {
      return query({
        query: 'spacesByPartnerIdByUpdatedAt',
        variables: {
          partner_id: id,
          filter: {
            context: { eq: 'EMPLOYEE' }
          }
        }
      })
    }
  })

  const capabilities = useSchemaCapabilities()
  const withPlan = capabilities.paymentPlan

  const paymentsQuery = useQuery({
    queryKey: paymentsQueryKey(id, withPlan),
    queryFn: () =>
      query({
        query: paymentsQueryName(withPlan),
        variables: { partner_id: id, sortDirection: 'DESC' }
      }),
    // Erst nach der Probe, sonst liefe die Liste einmal ohne plan
    enabled: !capabilities.isLoading
  })
  const payments = sortPaymentsDesc(paymentsQuery.data)
  const isLoadingPayment = capabilities.isLoading || paymentsQuery.isLoading

  const { flagsQuery, saveFlags } = useFeatureFlags()
  const flag = flagsQuery.isError ? undefined : pricingFlagOf(flagsQuery.data)
  // lead_ampel steht nicht im PARTNER-Set. Ohne das Feld meldet die
  // Auswertung bei Targeting darauf partial statt eines falschen Ergebnisses.
  const flagPartner =
    customer && Object.prototype.hasOwnProperty.call(customer, 'lead_ampel')
      ? customer
      : undefined
  const flagCtx = { stage: STAGE, partner: flagPartner }
  const enforcement = pricingEnforcementState({
    flag,
    partnerId: id,
    payments,
    // Bei fehlgeschlagener Probe sind die Payments ohne plan geladen
    plansKnown: !capabilities.isError,
    ...flagCtx
  })
  const flagPercent =
    flag !== undefined ? describePricingFlag(flag, id, flagCtx).percent : null
  const roles = paymentRolesOf(payments, enforcement)
  const { periodPayment } = selectPayments(payments)

  const customerName = customer?.display_name || customer?.name

  const pages = [
    { name: 'Kunden', to: '/customers', current: false },
    { name: customerName, to: '#', current: true }
  ]

  const challenges = JSON.parse(customer?.app_settings?.challenges || '[]')

  // Derived state: Calculate discover status directly from customer data
  const dashboardSettings = parseDashboardSettings(
    customer?.app_settings?.dashboard_settings
  )
  const discover = !!dashboardSettings?.experimental?.components?.discover

  const [savingDashboardSettings, setSavingDashboardSettings] = useState(false)

  const isReady =
    !isLoading && !isLoadingTeammember && !isLoadingPayment && !!customer

  // Links aus Slack und der Migrations-Übersicht zeigen auf #paket
  useEffect(() => {
    if (!isReady || location.hash !== '#paket') return
    const target = document.getElementById('paket')
    if (target && typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ block: 'start' })
    }
  }, [isReady, location.hash])

  const handleToggleDiscover = async () => {
    setSavingDashboardSettings(true)

    try {
      const currentSettings = parseDashboardSettings(
        customer?.app_settings?.dashboard_settings
      )

      // Update nested structure
      if (!currentSettings.experimental) {
        currentSettings.experimental = {}
      }
      if (!currentSettings.experimental.components) {
        currentSettings.experimental.components = {}
      }
      currentSettings.experimental.components.discover = !discover

      await updatePartnerAppSettings({
        id: customer.id,
        app_settings: {
          ...customer.app_settings,
          dashboard_settings: JSON.stringify(currentSettings)
        }
      })

      // Invalidate and refetch customer data
      await queryClient.invalidateQueries(['customer', id])
    } catch (err) {
      error(
        `Dashboard Settings konnten nicht gespeichert werden: ${extractErrorMessage(err)}`
      )
    } finally {
      setSavingDashboardSettings(false)
    }
  }

  if (isLoading || isLoadingTeammember || isLoadingPayment) {
    return <Loader loadingText={'Kunde wird geladen ...'} />
  }

  // Nach einer Löschung ist die Seite per Browser-Back weiter erreichbar.
  if (!customer) {
    return (
      <div className='bg-white shadow sm:rounded-lg px-4 py-8 text-center'>
        <h2 className='text-sm font-semibold text-gray-900'>
          Kunde nicht gefunden
        </h2>
        <p className='mt-1 text-sm text-gray-500'>
          Der Kunde mit der ID {id} existiert nicht (mehr).
        </p>
        <Link
          to='/customers'
          className='mt-4 inline-block text-sm font-medium text-indigo-600 hover:text-indigo-500'
        >
          Zurück zur Kundenliste
        </Link>
      </div>
    )
  }

  const linkState = { customerName, customerId: id }

  return (
    <div>
      <nav className='flex mb-8' aria-label='Breadcrumb'>
        <ol className='flex items-center space-x-4'>
          <li>
            <div>
              <Link to={'/'} className='text-gray-400 hover:text-gray-500'>
                <HomeIcon
                  className='h-5 w-5 flex-shrink-0'
                  aria-hidden='true'
                />
                <span className='sr-only'>Home</span>
              </Link>
            </div>
          </li>
          {pages.map((page) => (
            <li key={page.name}>
              <div className='flex items-center'>
                <svg
                  className='h-5 w-5 flex-shrink-0 text-gray-300'
                  fill='currentColor'
                  viewBox='0 0 20 20'
                  aria-hidden='true'
                >
                  <path d='M5.555 17.776l8-16 .894.448-8 16-.894-.448z' />
                </svg>
                <Link
                  to={page.to}
                  className='ml-4 text-sm font-medium text-gray-500 hover:text-gray-700'
                  aria-current={page.current ? 'page' : undefined}
                >
                  {page.name}
                </Link>
              </div>
            </li>
          ))}
        </ol>
      </nav>
      <header className='mb-8'>
        <h1 className='text-3xl font-bold leading-tight tracking-tight text-gray-900'>
          {customer.display_name || customer.name}
        </h1>
      </header>
      <div className='mx-auto grid max-w-3xl grid-cols-1 lg:max-w-7xl'>
        <section aria-labelledby='applicant-information-title'>
          <div className='bg-white shadow sm:rounded-lg '>
            <div className='px-4 py-5 sm:px-6 flex flex-row justify-between'>
              <div>
                <h2
                  id='applicant-information-title'
                  className='text-lg font-medium leading-6 text-gray-900'
                >
                  Kundeninformation
                </h2>
                <p className='mt-1 max-w-2xl text-sm text-gray-500'>
                  Informationen, Payment, Teams etc.
                </p>
              </div>
              {customer.logo && (
                <img
                  alt='customer logo'
                  src={customer.logo}
                  className='h-12 w-28 object-contain '
                />
              )}
            </div>
            <div className='border-t border-gray-200 px-4 py-5 sm:px-6'>
              <dl className='grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2'>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>ID</dt>
                  <dd className='mt-1 text-sm text-gray-900'>{customer.id}</dd>
                </div>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>Name</dt>
                  <dd className='mt-1 text-sm text-gray-900'>
                    {customer.name}
                  </dd>
                </div>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>
                    Karriere Webseite URL
                  </dt>
                  <dd className='mt-1 text-sm text-gray-900'>
                    <a
                      className='text-indigo-600 hover:font-semibold'
                      href={customer.career_site_url}
                      target='_blank'
                      rel='noreferrer'
                    >
                      {customer.career_site_url || '-'}
                    </a>
                  </dd>
                </div>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>
                    Challenges im Partner festgelegt:
                  </dt>
                  <dd className='mt-1 text-sm text-gray-900'>
                    {challenges.length === 0 && '-'}
                    <div className='flex flex-row gap-2 flex-wrap'>
                      {challenges.map((c, index) => (
                        <span
                          key={index}
                          className='inline-flex items-center rounded-md bg-indigo-100 px-2 py-1 text-xs font-medium text-indigo-700'
                        >
                          {c.exam_id}
                        </span>
                      ))}
                    </div>
                  </dd>
                </div>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>
                    Erstellt:
                  </dt>
                  <dd className='mt-1 text-sm text-gray-900'>
                    {format(new Date(customer.createdAt), 'dd.MM.yyyy')}
                  </dd>
                </div>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>
                    Zuletzt geändert:
                  </dt>
                  <dd className='mt-1 text-sm text-gray-900'>
                    {format(new Date(customer.updatedAt), 'dd.MM.yyyy')}
                  </dd>
                </div>
                {/* <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-500">About</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    Fugiat ipsum ipsum deserunt culpa aute sint do nostrud anim
                    incididunt cillum culpa consequat. Excepteur qui ipsum
                    aliquip consequat sint. Sit id mollit nulla mollit nostrud
                    in ea officia proident. Irure nostrud pariatur mollit ad
                    adipisicing reprehenderit deserunt qui eu.
                  </dd>
                </div> */}
                {/* <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-500">
                    Attachments
                  </dt>
                </div> */}
              </dl>
            </div>
          </div>
        </section>
      </div>
      {/* Dashboard Settings Section */}
      <section className='mt-4' aria-labelledby='dashboard-settings'>
        <div className='bg-white shadow sm:rounded-lg '>
          <div className='px-4 py-5 sm:px-6 flex flex-row justify-between'>
            <div>
              <h2
                id='dashboard-settings'
                className='text-lg font-medium leading-6 text-gray-900'
              >
                Dashboard Settings
              </h2>
              <p className='mt-1 max-w-2xl text-sm text-gray-500'>
                Aktiviere oder deaktiviere experimentelle Features für diesen
                Kunden.
              </p>
            </div>
          </div>
          <div className='border-t border-gray-200 px-4 py-5 sm:px-6'>
            <div className='flex items-center gap-4'>
              <span className='text-sm font-medium text-gray-700'>
                Discover-Feature
              </span>
              <button
                onClick={handleToggleDiscover}
                disabled={savingDashboardSettings}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${discover ? 'bg-indigo-600' : 'bg-gray-200'}`}
                type='button'
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${discover ? 'translate-x-6' : 'translate-x-1'}`}
                />
              </button>
              <span className='text-xs text-gray-500'>
                {discover ? 'Aktiviert' : 'Deaktiviert'}
              </span>
              {savingDashboardSettings && (
                <BeatLoader color='#67e8f9' size={10} />
              )}
            </div>
            <p className='text-xs text-gray-400 mt-2'>
              Steuert, ob das Discover-Feature im Dashboard für diesen Kunden
              sichtbar ist.
            </p>
          </div>
        </div>
      </section>

      <section className='mt-4' aria-labelledby='teammember'>
        <div className='bg-white shadow sm:rounded-lg '>
          <div className='px-4 py-5 sm:px-6 flex flex-row justify-between'>
            <div>
              <h2
                id='teammember'
                className='text-lg font-medium leading-6 text-gray-900'
              >
                Teammitglieder
              </h2>
            </div>
          </div>
          <div className='border-t border-gray-200 px-4 py-5 sm:px-6'>
            <ul className='divide-y divide-gray-100'>
              {(teammember || []).map((tm) => {
                const person = tm.person || {}
                const firstname =
                  person.firstname || person.invitation_firstname
                const lastname = person.lastname || person.invitation_lastname
                const email = person.email || person.invitation_email

                return (
                  <li key={tm.id} className='flex gap-x-4 py-5'>
                    <span className='inline-flex items-center justify-center h-10 w-10 rounded-full bg-gray-500'>
                      <span className='font-medium leading-none text-white'>
                        {initials(`${firstname} ${lastname}`)}
                      </span>
                    </span>

                    <div className='min-w-0'>
                      <p className='text-sm font-semibold leading-6 text-gray-900'>
                        {`${firstname} ${lastname}`}
                      </p>
                      <p className='mt-1 truncate text-xs leading-5 text-gray-500'>
                        {`${email || tm.id}`}
                      </p>
                      {tm.last_seen && (
                        <p className='mt-1 text-xs text-gray-500'>
                          {`Zuletzt online: ${formatDistance(
                            new Date(tm.last_seen),
                            new Date(),
                            { locale: de }
                          )}`}
                        </p>
                      )}
                    </div>
                  </li>
                )
              })}
            </ul>
          </div>
        </div>
      </section>

      <PlanUsageSection
        partnerId={id}
        customerName={customerName}
        payments={payments}
        paymentsQuery={paymentsQuery}
        capabilities={capabilities}
        flagsQuery={flagsQuery}
        saveFlags={saveFlags}
        enforcement={enforcement}
        flagPercent={flagPercent}
        isAdmin={isAdmin}
        owner={owner}
      />

      <section className='mt-4' aria-labelledby='payment'>
        <div className='bg-white shadow sm:rounded-lg '>
          <div className='px-4 py-5 sm:px-6 flex flex-row justify-between'>
            <div className='flex flex-row justify-between items-center w-full'>
              <h2
                id='payment'
                className='text-lg font-medium leading-6 text-gray-900'
              >
                Hinterlegte Zahlungen
              </h2>
              {payments.length > 0 && (
                <NewPaymentLink
                  partnerId={id}
                  fromPaymentId={periodPayment?.id}
                  state={linkState}
                />
              )}
            </div>
          </div>
          <div className='border-t border-gray-200 px-4 py-5 sm:px-6'>
            {paymentsQuery.isError && (
              <Notice tone='error'>
                Zahlungen konnten nicht geladen werden.{' '}
                {extractErrorMessage(paymentsQuery.error)}{' '}
                <RetryButton
                  onClick={() => paymentsQuery.refetch()}
                  isLoading={paymentsQuery.isFetching}
                />
              </Notice>
            )}
            <ul className='divide-y divide-gray-100'>
              {!paymentsQuery.isError && payments.length === 0 && (
                <div className='text-center'>
                  <svg
                    className='mx-auto h-12 w-12 text-gray-400'
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                    aria-hidden='true'
                  >
                    <path
                      vectorEffect='non-scaling-stroke'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z'
                    />
                  </svg>
                  <h3 className='mt-2 text-sm font-semibold text-gray-900'>
                    Keine Zahlung hinterlegt
                  </h3>
                  <p className='mt-1 text-sm text-gray-500'>
                    Für diesen Kunden wurde keine Zahlung hinterlegt.
                  </p>
                  <div className='mt-6'>
                    <NewPaymentLink partnerId={id} state={linkState} />
                  </div>
                </div>
              )}
              {payments.map((payment) => (
                <li key={payment.id}>
                  <Link
                    to={`/customer/payment/${payment.id}`}
                    state={linkState}
                    className='cursor-pointer relative flex flex-wrap justify-between gap-x-6 gap-y-3 px-4 py-5 hover:bg-gray-50 sm:px-6 lg:px-8'
                    data-testid='payment-row'
                  >
                    <div className='w-56 shrink-0'>
                      <PaymentKindBadge payment={payment} />
                      {(roles[payment.id] || []).length > 0 && (
                        <div className='mt-2 flex flex-wrap gap-1'>
                          {roles[payment.id].map((role) => (
                            <RoleChip key={role} role={role} />
                          ))}
                        </div>
                      )}
                    </div>
                    <div>
                      <p className='text-base font-semibold leading-6 text-gray-900'>
                        startet am
                      </p>
                      <p className='text-sm text-gray-500'>
                        {formatDate(payment.start_at)}
                      </p>
                    </div>
                    <div className='mx-4'>
                      <p className='text-base font-semibold leading-6 text-gray-900'>
                        gültig bis
                      </p>
                      <p
                        className={classNames(
                          'text-sm',
                          isExpired(payment.expires_at)
                            ? 'text-red-600'
                            : 'text-gray-500'
                        )}
                      >
                        {formatDate(payment.expires_at)}
                      </p>
                    </div>
                    <div className='min-w-0 flex-auto'>
                      <p className='text-base font-semibold leading-6 text-gray-900'>
                        Paket
                      </p>
                      <p className='text-sm text-gray-500'>
                        {planColumnLabel(payment)}
                      </p>
                    </div>
                    <div className='flex shrink-0 items-center gap-x-4'>
                      <div>
                        <p className='text-base font-semibold leading-6 text-gray-900'>
                          Preis
                        </p>
                        <p className='text-sm text-gray-500'>
                          {monthlyPriceLabel(payment)}
                        </p>
                      </div>
                      <ChevronRightIcon
                        className='h-5 w-5 flex-none text-gray-400'
                        aria-hidden='true'
                      />
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
            <RoleLegend roles={roles} />
          </div>
        </div>
      </section>

      {/* Gefahrenzone */}
      <section className='mt-4' aria-labelledby='danger-zone'>
        <div className='bg-white shadow sm:rounded-lg ring-1 ring-red-600/20'>
          <div className='px-4 py-5 sm:px-6'>
            <h2
              id='danger-zone'
              className='text-lg font-medium leading-6 text-red-700'
            >
              Gefahrenzone
            </h2>
            <p className='mt-1 max-w-2xl text-sm text-gray-500'>
              Aktionen in diesem Bereich sind endgültig und können nicht
              rückgängig gemacht werden.
            </p>
          </div>
          <div className='border-t border-red-100 px-4 py-5 sm:px-6 sm:flex sm:items-start sm:justify-between sm:gap-6'>
            <div>
              <h3 className='text-sm font-semibold text-gray-900'>
                Kunden löschen
              </h3>
              <p className='mt-1 text-sm text-gray-500'>
                Löscht „{customerName}“ dauerhaft aus Aivy. Vor dem Löschen
                zeigt eine Vorschau, was betroffen ist, und ein JSON-Backup wird
                heruntergeladen.
              </p>
              <ul className='mt-2 list-disc space-y-1 pl-5 text-sm text-gray-500'>
                <li>
                  Teammitglieder verlieren sofort den Zugang; Space und Person
                  werden gelöscht, der Cognito-Login bleibt bestehen.
                </li>
                <li>
                  Zahlungen, Integrationen, API-Keys und Nutzungszähler werden
                  gelöscht, Cron-Jobs neutralisiert.
                </li>
                <li>
                  Stellen werden archiviert und an den Aivy-Standardpartner
                  übertragen; Talent-Assessments werden dorthin umgehängt, die
                  Talente behalten ihre Ergebnisse.
                </li>
                <li>Logs erhalten ein Ablaufdatum.</li>
                <li>Eine aktive, bezahlte Zahlung blockiert das Löschen.</li>
              </ul>
            </div>
            <div className='mt-4 sm:mt-0 sm:flex-shrink-0'>
              <Button.DangerLG
                text='Kunden löschen'
                onClick={() => setDeleteOpen(true)}
              />
            </div>
          </div>
        </div>
      </section>
      <DeletePartnerModal
        open={deleteOpen}
        setOpen={setDeleteOpen}
        partner={customer}
      />
    </div>
  )
}

export default Customer
